export function CaseStudies() {
  const caseStudies = [
    {
      title: 'Vacant Duplex in Detroit, Michigan',
      propertyType: 'Duplex (2 Units)',
      situation: 'The owner inherited this 1920s duplex from a family member. Both units had been vacant for over a year, and the property needed significant repairs including a new roof, updated electrical, and plumbing work. The owner lived out of state and couldn\'t manage the renovation.',
      challenge: 'Traditional buyers were scared off by the extensive repair needs and vacant status. Real estate agents suggested listing at a very low price or investing $50,000+ in repairs first.',
      solution: 'We purchased the property as-is for a fair cash price that reflected its potential value. The owner avoided the stress and expense of long-distance property management and costly repairs.',
      outcome: 'Closed in 10 days. The seller received $127,000 cash and avoided an estimated $50,000 in repair costs and 6-12 months of carrying costs.',
      image: 'https://public.readdy.ai/ai/img_res/ad74d37b68df46909463005d85156248.jpg'
    },
    {
      title: 'Problem Tenant Triplex in Phoenix, Arizona',
      propertyType: 'Triplex (3 Units)',
      situation: 'The owner had been dealing with a problem tenant in one unit for over a year. The tenant was consistently late with rent, had caused property damage, and the eviction process was dragging on. The other two units were occupied by good tenants.',
      challenge: 'The owner was exhausted from the tenant issues and wanted out, but traditional buyers wouldn\'t make offers until the problem tenant was removed and repairs were completed.',
      solution: 'We purchased the property with the problem tenant in place. Our team handled the tenant situation professionally after closing, allowing the seller to walk away immediately.',
      outcome: 'Closed in 14 days. The seller received $385,000 and was relieved of all tenant management responsibilities. We successfully resolved the tenant issue within 60 days of closing.',
      image: 'https://readdy.ai/api/search-image?query=triplex%20apartment%20building%20three%20units%20in%20southwestern%20style%2C%20multifamily%20residential%20property%2C%20professional%20real%20estate%20photography%2C%20rental%20property%20with%20clean%20simple%20background&width=600&height=400&seq=case-triplex-001&orientation=landscape'
    },
    {
      title: 'Outdated Quadplex in Cleveland, Ohio',
      propertyType: 'Quadplex (4 Units)',
      situation: 'This 1960s quadplex had original kitchens, bathrooms, and mechanical systems. All four units were occupied by long-term tenants paying below-market rents. The owner wanted to retire and move to Florida but knew the property needed major updates.',
      challenge: 'The property was generating income but needed $80,000+ in renovations to achieve market rents. Traditional buyers wanted the seller to complete updates first or offered very low prices.',
      solution: 'We made a fair cash offer that accounted for the needed renovations while recognizing the property\'s strong location and stable tenant base. The seller could retire on schedule without managing a major renovation project.',
      outcome: 'Closed in 12 days. The seller received $445,000 cash and retired to Florida as planned. We completed renovations over 18 months as leases naturally expired.',
      image: 'https://readdy.ai/api/search-image?query=older%20quadplex%20fourplex%20apartment%20building%20from%201960s%2C%20vintage%20multifamily%20property%2C%20occupied%20rental%20building%2C%20investment%20property%20with%20clean%20simple%20background&width=600&height=400&seq=case-quadplex-001&orientation=landscape'
    },
    {
      title: 'Foreclosure-Threatened 5-Unit in Atlanta, Georgia',
      propertyType: '5-Unit Apartment Building',
      situation: 'The owner had fallen behind on mortgage payments due to unexpected medical expenses. The property was 60 days from foreclosure. Three units were occupied, two were vacant. The owner needed to sell immediately to avoid foreclosure and protect their credit.',
      challenge: 'Time was critical. Traditional sales take 3-6 months, and the owner had less than 60 days. The property\'s partial vacancy and the owner\'s financial distress made conventional financing difficult for buyers.',
      solution: 'We moved quickly with an all-cash offer and expedited closing process. Our ability to close fast allowed the owner to pay off the mortgage and avoid foreclosure.',
      outcome: 'Closed in 9 days. The seller received $520,000, paid off the mortgage, avoided foreclosure, and protected their credit score. They had funds remaining to address their medical expenses.',
      image: 'https://readdy.ai/api/search-image?query=five%20unit%20apartment%20building%20small%20multifamily%20property%2C%20residential%20rental%20complex%2C%20professional%20real%20estate%20photography%2C%20investment%20property%20with%20clean%20simple%20background&width=600&height=400&seq=case-five-unit-001&orientation=landscape'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Real Success Stories from Small Apartment Building Sellers
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            See how we've helped property owners successfully sell their duplexes, triplexes, quadplexes, and small apartment buildings quickly and profitably.
          </p>
        </div>

        <div className="space-y-12">
          {caseStudies.map((study, index) => (
            <div key={index} className="bg-gradient-to-br from-amber-50 to-white rounded-2xl shadow-xl overflow-hidden">
              <div className="grid lg:grid-cols-2 gap-8">
                <div className={`p-8 lg:p-12 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="inline-block px-4 py-2 bg-orange-500 text-white rounded-full text-sm font-semibold mb-4">
                    {study.propertyType}
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-6">{study.title}</h3>
                  
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                        <i className="ri-file-list-3-line text-orange-500 mr-2"></i>
                        The Situation
                      </h4>
                      <p className="text-gray-600">{study.situation}</p>
                    </div>

                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                        <i className="ri-error-warning-line text-red-500 mr-2"></i>
                        The Challenge
                      </h4>
                      <p className="text-gray-600">{study.challenge}</p>
                    </div>

                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                        <i className="ri-lightbulb-line text-orange-500 mr-2"></i>
                        Our Solution
                      </h4>
                      <p className="text-gray-600">{study.solution}</p>
                    </div>

                    <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                      <h4 className="text-lg font-bold text-green-800 mb-2 flex items-center">
                        <i className="ri-checkbox-circle-line mr-2"></i>
                        The Outcome
                      </h4>
                      <p className="text-green-800 font-semibold">{study.outcome}</p>
                    </div>
                  </div>
                </div>

                <div className={`relative min-h-[400px] ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <img 
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-8 lg:p-12 text-white max-w-4xl mx-auto">
            <h3 className="text-3xl lg:text-4xl font-bold mb-6">
              Your Success Story Starts Here
            </h3>
            <p className="text-xl mb-8 opacity-95">
              Join hundreds of satisfied sellers who have successfully sold their small apartment buildings to us. Get your free, no-obligation cash offer today.
            </p>
            <a 
              href="#get-offer"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-orange-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Get Your Cash Offer Now
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}